import { isBeta } from "../stateManagers/apiSetup/env";


export interface RouteConfig {
  path: string;
  label: string;
  icon?: string;
  component?: string;
  children?: Record<string, RouteConfig>;
  dynamicLabel?: (context: Record<string, any>) => string;
  hideInSidebar?: boolean;
}

export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  dashboard: {
    path: '/',
    label: 'Dashboard',
    icon: 'pi-home',
    component: 'Dashboard'
  },

  instituteProfile: {
    path: '/institute-profile',
    label: 'Institute Profile',
    icon: 'pi-home',
    component: 'InstituteProfile'
  },

  branches: {
    path: '/branches',
    label: 'Branches',
    icon: 'pi-building',
    component: 'Branches',
    children: {
      branchDetails: {
        path: '/branches/:branchId',
        label: 'Branch Details',
        component: 'BranchDetails',
        hideInSidebar: true,
        dynamicLabel: (context) => context.branchName || 'Branch Details'
      },
      branchStudents: {
        path: '/branches/:branchId/students',
        label: 'Students',
        component: 'BranchStudents',
        hideInSidebar: true,
        dynamicLabel: (context) => context.branchName ? `${context.branchName} — Students` : 'Students'
      }
    }
  },

  coursesandsubjects: {
    path: '/courses',
    label: 'Courses & Subjects',
    icon: 'pi-pen-to-square',
    component: 'Courses',
    children: {
      databaseSetup: {
        path: '/courses-and-subjects/courses',
        label: 'Courses',
        icon: 'pi-file-plus',
        component: 'Courses'
      },
      setup: {
        path: '/courses-and-subjects/subjects',
        label: 'Subjects',
        icon: 'pi-book',
        component: 'Subjects'
      }
    }
  },

  studymaterials: {
    path: '/study-materials',
    label: 'StudyMaterials',
    icon: 'pi-book',
    component: 'StudyMaterials'
  },
  
    practicalmaterials: {
    path: '/practical-materials',
    label: 'Practical Materials',
    icon: 'pi-file',
    component: 'PracticalMaterials'
  },

  medialibrary: {
    path: '/media-library',
    label: 'Media Library',
    icon: 'pi-images',
    component: 'MediaLibrary'
  },

  licensemanagement: {
    path: '/student-issues',
    label: 'License Managment',
    icon: 'pi-info-circle',
    component: 'StudentIssues'
  },

  ...(isBeta && {
    backupPanel: {
      path: '/backup-panel',
      label: 'Backup Panel',
      icon: 'pi-database',
      component: 'BackupPanel'
    }
  })
};

// ── everything below stays exactly the same as your original ──

export interface BreadcrumbItem {
  id: string;
  label: string;
  path: string;
}

const flattenRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): Array<{ id: string; route: RouteConfig; parent?: string }> => {
  const flattened: Array<{ id: string; route: RouteConfig; parent?: string }> = [];
  for (const [id, route] of Object.entries(config)) {
    flattened.push({ id, route, parent: parentId });
    if (route.children) {
      flattened.push(...flattenRoutes(route.children, id));
    }
  }
  return flattened;
};

const FLATTENED_ROUTES = flattenRoutes();

export const findRouteByPath = (
  path: string
): { id: string; route: RouteConfig; parent?: string } | null => {
  const sortedRoutes = [...FLATTENED_ROUTES].sort((a, b) =>
    b.route.path.length - a.route.path.length
  );
  for (const { id, route, parent } of sortedRoutes) {
    if (route.path === path) return { id, route, parent };
    const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pathPattern}$`);
    if (regex.test(path)) return { id, route, parent };
  }
  return null;
};

export const findRouteById = (
  id: string,
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): { route: RouteConfig; parent?: string } | null => {
  if (config[id]) return { route: config[id], parent: parentId };
  for (const [currentId, route] of Object.entries(config)) {
    if (route.children) {
      const result = findRouteById(id, route.children, currentId);
      if (result) return result;
    }
  }
  return null;
};

export const buildBreadcrumbTrail = (
  currentPath: string,
  context: Record<string, any> = {}
): BreadcrumbItem[] => {
  const trail: BreadcrumbItem[] = [];
  trail.push({ id: 'dashboard', label: 'Dashboard', path: '/' });
  const current = findRouteByPath(currentPath);
  if (!current || current.id === 'dashboard') return trail;
  const buildChain = (parentId?: string): string[] => {
    if (!parentId) return [];
    const parent = findRouteById(parentId);
    if (!parent) return [];
    const chain = buildChain(parent.parent);
    chain.push(parentId);
    return chain;
  };
  const parentChain = buildChain(current.parent);
  for (const parentId of parentChain) {
    const parentResult = findRouteById(parentId);
    if (parentResult) {
      const label = parentResult.route.dynamicLabel
        ? parentResult.route.dynamicLabel(context)
        : parentResult.route.label;
      trail.push({ id: parentId, label, path: parentResult.route.path });
    }
  }
  const label = current.route.dynamicLabel
    ? current.route.dynamicLabel(context)
    : current.route.label;
  trail.push({ id: current.id, label, path: current.route.path });
  return trail;
};

export const getSidebarRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG
): Array<{ id: string; route: RouteConfig; children?: Array<{ id: string; route: RouteConfig }> }> => {
  const routes: Array<{
    id: string;
    route: RouteConfig;
    children?: Array<{ id: string; route: RouteConfig }>;
  }> = [];
  for (const [id, route] of Object.entries(config)) {
    if (route.hideInSidebar) continue;
    const item: any = { id, route };
    if (route.children) {
      item.children = [];
      for (const [childId, childRoute] of Object.entries(route.children)) {
        if (!childRoute.hideInSidebar) {
          item.children.push({ id: childId, route: childRoute });
        }
      }
    }
    routes.push(item);
  }
  return routes;
};

export const isRouteActive = (routeId: string, currentPath: string): boolean => {
  const current = findRouteByPath(currentPath);
  if (!current) return false;
  if (current.id === routeId) return true;
  const buildParentChain = (parentId?: string): string[] => {
    if (!parentId) return [];
    const parent = findRouteById(parentId);
    if (!parent) return [];
    const chain = buildParentChain(parent.parent);
    chain.push(parentId);
    return chain;
  };
  const parentChain = buildParentChain(current.parent);
  return parentChain.includes(routeId);
};

export const getPathForRoute = (
  routeId: string,
  params: Record<string, string> = {}
): string => {
  const result = findRouteById(routeId);
  if (!result) return '/';
  let path = result.route.path;
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value);
  }
  return path;
};